import type { ReactNode } from 'react'
import React, { createContext, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import type { SelectOption } from '@fourthwall/components'
import { ConfirmModal, useModalContext, useToastsContext } from '@fourthwall/components'
import type { useFormik } from '@fourthwall/utils/dist/formik'
import { useMembersQuery } from '@modules/Dashboard/Memberships/modules/Members/hooks/useMembersQuery'
import { useTiersQuery } from '@modules/Dashboard/Memberships/modules/Perks/hooks/useTiersQuery'
import { useUsersQuery } from '@queries/useUsersQuery'
import { routing } from '@utils/routing'

import { useBadgesQuery } from '../../../../hooks/useBadgesQuery'
import { useDeleteCustomBadgeMutation } from '../../../../hooks/useDeleteCustomBadgeMutation'
import { useDisableCustomBadgeMutation } from '../../../../hooks/useDisableCustomBadgeMutation'
import { useDisableMemberStreakBadgeMutation } from '../../../../hooks/useDisableMemberStreakBadgeMutation'
import { useDisableVerifiedBadgeMutation } from '../../../../hooks/useDisableVerifiedBadgeMutation'
import { useEnableCustomBadgeMutation } from '../../../../hooks/useEnableCustomBadgeMutation'
import { useEnableMemberStreakBadgeMutation } from '../../../../hooks/useEnableMemberStreakBadgeMutation'
import { useEnableVerifiedBadgeMutation } from '../../../../hooks/useEnableVerifiedBadgeMutation'
import { getEmojiFormValue } from '../../../../utils'
import type { FormValues, Rules } from '../../hooks/useBadgeForm/types'
import { useBadgeForm } from '../../hooks/useBadgeForm/useBadgeForm'
import { useEditCustomBadgeMutation } from '../../hooks/useEditCustomBadgeMutation'
import { useEditMemberStreakBadgeMutation } from '../../hooks/useEditMemberStreakBadgeMutation'
import { useEditVerifiedBadgeMutation } from '../../hooks/useEditVerifiedBadgeMutation'
import { getIconPayload, getRulesPayload } from '../../utils'

type Props = { children: ReactNode }

type BadgeContextType = {
  badge?: NonNullable<ReturnType<typeof useBadgesQuery>['badgesQuery']['data']>['badges'][number]
  tiers?: NonNullable<ReturnType<typeof useTiersQuery>['tiersQuery']['data']>['tiers']
  form: ReturnType<typeof useFormik<FormValues>>
  availableRules: SelectOption[]
  hasEmptyRule: boolean
  hasAnyRules: boolean
  hasAvailableRules: boolean
  usersQuery: ReturnType<typeof useUsersQuery>['usersQuery']
  isFetching: boolean
  isLoading: boolean
  enableBadge: () => void
  disableBadge: () => void
  deleteBadge: () => void
  getRuleComponent: (rule: Rules[number]) => React.JSX.Element
}

export const BadgeContext = createContext<BadgeContextType | undefined>(undefined)

export const BadgeProvider = ({ children }: Props) => {
  const { badgeId = '' } = useParams<'badgeId'>()

  const navigate = useNavigate()
  const modal = useModalContext()
  const toast = useToastsContext()

  const { badgesQuery } = useBadgesQuery()
  const { tiersQuery } = useTiersQuery()

  const { editCustomBadgeMutation } = useEditCustomBadgeMutation()
  const { editVerifiedBadgeMutation } = useEditVerifiedBadgeMutation()
  const { enableCustomBadgeMutation } = useEnableCustomBadgeMutation()
  const { disableCustomBadgeMutation } = useDisableCustomBadgeMutation()
  const { deleteCustomBadgeMutation } = useDeleteCustomBadgeMutation()
  const { enableVerifiedBadgeMutation } = useEnableVerifiedBadgeMutation()
  const { disableVerifiedBadgeMutation } = useDisableVerifiedBadgeMutation()
  const { editMemberStreakBadgeMutation } = useEditMemberStreakBadgeMutation()
  const { enableMemberStreakBadgeMutation } = useEnableMemberStreakBadgeMutation()
  const { disableMemberStreakBadgeMutation } = useDisableMemberStreakBadgeMutation()

  const badge = badgesQuery.data?.badges.find((badge) => badge.id === parseInt(badgeId))
  const customMemberStreakBadges = badgesQuery.data?.badges.filter(
    (badge) => badge.type === 'MemberStreakCustom',
  )

  const { membersQuery } = useMembersQuery(
    [
      {
        page: 1,
        limit: 9999,
        'member_ids[]': badge?.manuallyAssignedSupporterIds || [],
      },
    ],
    {
      cacheTime: Infinity,
      enabled: badge?.manuallyAssignedSupporterIds
        ? badge?.manuallyAssignedSupporterIds.length > 0
        : false,
    },
  )

  const { usersQuery } = useUsersQuery()

  const enableBadge = useCallback(() => {
    modal.open(ConfirmModal, {
      title: 'Are you sure you want to enable this badge?',
      confirmLabel: 'Enable',
      onConfirm: () => {
        if (badge?.type === 'Custom') {
          enableCustomBadgeMutation.mutate([{ id: parseInt(badgeId) }], {
            onSuccess: badgesQuery.refetch,
          })
        } else if (badge?.type === 'MemberStreakDefault') {
          enableMemberStreakBadgeMutation.mutate([], {
            onSuccess: badgesQuery.refetch,
          })
        } else if (badge?.type === 'Verified') {
          enableVerifiedBadgeMutation.mutate([], {
            onSuccess: badgesQuery.refetch,
          })
        }
      },
    })
  }, [
    badgeId,
    badge?.type,
    badgesQuery,
    enableCustomBadgeMutation,
    enableMemberStreakBadgeMutation,
    enableVerifiedBadgeMutation,
    modal,
  ])

  const disableBadge = useCallback(() => {
    modal.open(ConfirmModal, {
      title: 'Are you sure you want to disable this badge?',
      confirmLabel: 'Disable',
      onConfirm: () => {
        if (badge?.type === 'Custom') {
          disableCustomBadgeMutation.mutate([{ id: parseInt(badgeId) }], {
            onSuccess: badgesQuery.refetch,
          })
        } else if (badge?.type === 'MemberStreakDefault') {
          disableMemberStreakBadgeMutation.mutate([], {
            onSuccess: badgesQuery.refetch,
          })
        } else if (badge?.type === 'Verified') {
          disableVerifiedBadgeMutation.mutate([], {
            onSuccess: badgesQuery.refetch,
          })
        }
      },
    })
  }, [
    badgeId,
    badge?.type,
    badgesQuery,
    disableCustomBadgeMutation,
    disableMemberStreakBadgeMutation,
    modal,
  ])

  const deleteBadge = useCallback(() => {
    modal.open(ConfirmModal, {
      title: 'Are you sure you want to delete this badge?',
      confirmLabel: 'Delete',
      onConfirm: () => {
        deleteCustomBadgeMutation.mutate([{ id: parseInt(badgeId) }], {
          onSuccess: () => navigate(routing.memberships.settings.badges.self),
        })
      },
    })
  }, [badgeId, deleteCustomBadgeMutation, modal, navigate])

  const getBadgeType = useCallback(() => {
    if (badge?.type === 'Verified') {
      return 'Verified'
    }

    if (badge?.minimal) {
      return 'IconOnly'
    }

    return 'IconAndText'
  }, [badge])

  const { form, availableRules, hasAnyRules, hasAvailableRules, hasEmptyRule, getRuleComponent } =
    useBadgeForm({
      initialValues: {
        badgeType: getBadgeType(),
        text: badge?.text || '',
        emoji: badge
          ? getEmojiFormValue(badge?.icon)
          : {
              type: 'Emoji',
              emojiIcon: '😀',
            },
        borderColor: badge?.borderColor,
        backgroundColor: badge?.backgroundColor,
        textColor: badge?.textColor,
        useCustomUsernameColor: !!badge?.usernameColor,
        usernameTextColor: badge?.usernameColor,
        canBeEarnedByMembers: badge ? badge.rules.length > 0 : false,
        rules:
          badge?.rules.map((rule) => {
            if (rule.type === 'Tier') {
              return { type: 'Tier', tierId: rule.tierId }
            }

            if (rule.type === 'MemberStreak') {
              return { type: 'MemberStreak', monthsFrom: rule.monthsFrom, monthsTo: rule.monthsTo }
            }

            return { type: rule.type }
          }) || [],
        removeWhenRulesNoLongerMet: badge?.removeWhenRulesNoLongerMet || false,
        giveToSelectedMembers: !!badge?.manuallyAssignedSupporterIds?.length,
        giveToSelectedTeamStaff: badge
          ? badge?.creatorApplicationSettings?.type === 'ApplyToSelectedEmails'
          : false,
        verifiedApplyToType:
          badge?.creatorApplicationSettings?.type === 'ApplyToSelectedEmails' ? 'Selected' : 'All',
        manuallyAssignedSupporters: membersQuery.data ? membersQuery.data.members : [],
        manuallyAssignedTeamStaff:
          badge?.creatorApplicationSettings?.type === 'ApplyToSelectedEmails'
            ? badge?.creatorApplicationSettings?.emails
            : [],
        likesGivenMin: badge?.rules
          ?.find((rule) => rule.type === 'LikesGiven')
          // @ts-ignore
          ?.minimum.toString(),
        likesGivenMax: badge?.rules
          ?.find((rule) => rule.type === 'LikesGiven')
          // @ts-ignore
          ?.maximum?.toString(),
        likesReceivedMin: badge?.rules
          ?.find((rule) => rule.type === 'LikesReceived')
          // @ts-ignore
          ?.minimum.toString(),
        likesReceivedMax: badge?.rules
          ?.find((rule) => rule.type === 'LikesReceived')
          // @ts-ignore
          ?.maximum?.toString(),
        gifsGivenMin: badge?.rules
          ?.find((rule) => rule.type === 'GiftsGiven')
          // @ts-ignore
          ?.minimum.toString(),
        gifsGivenMax: badge?.rules
          ?.find((rule) => rule.type === 'GiftsGiven')
          // @ts-ignore
          ?.maximum?.toString(),
        firstSubscribedAtFrom:
          badge?.rules?.find((rule) => rule.type === 'FirstSubscribedAt')
            // prettier-ignore
            // @ts-ignore
            ?.firstSubscribedAtFrom,
        firstSubscribedAtTo:
          badge?.rules?.find((rule) => rule.type === 'FirstSubscribedAt')
            // prettier-ignore
            // @ts-ignore
            ?.firstSubscribedAtTo,
        activeMemberAtFrom:
          badge?.rules?.find((rule) => rule.type === 'ActiveMemberAt')
            // prettier-ignore
            // @ts-ignore
            ?.activeMemberAtFrom,
        activeMemberAtTo:
          badge?.rules?.find((rule) => rule.type === 'ActiveMemberAt')
            // prettier-ignore
            // @ts-ignore
            ?.activeMemberAtTo,
        commentsWrittenMin: badge?.rules
          ?.find((rule) => rule.type === 'CommentsWritten')
          // @ts-ignore
          ?.minimum.toString(),
        commentsWrittenMax: badge?.rules
          ?.find((rule) => rule.type === 'CommentsWritten')
          // @ts-ignore
          ?.maximum?.toString(),
        memberStreakBadges:
          customMemberStreakBadges?.map((badge) => {
            return {
              id: badge.id,
              emoji: getEmojiFormValue(badge.icon),
              borderColor: badge.borderColor,
              backgroundColor: badge.backgroundColor,
              textColor: badge.textColor,
              // @ts-ignore
              monthsFrom: badge.rules.find((rule) => rule.type === 'MemberStreak')?.monthsFrom || 0,
              // @ts-ignore
              monthsTo: badge.rules.find((rule) => rule.type === 'MemberStreak')?.monthsTo || 0,
            }
          }) || [],
      },
      onSubmit: (values) => {
        if (badge?.type === 'Custom') {
          editCustomBadgeMutation.mutate(
            [
              {
                id: parseInt(badgeId),
              },
              {
                text: values.text,
                icon: getIconPayload(values.emoji),
                borderColor: values.borderColor,
                backgroundColor: values.backgroundColor,
                textColor: values.textColor,
                minimal: values.badgeType === 'IconOnly',
                usernameColor: values.useCustomUsernameColor ? values.usernameTextColor : undefined,
                rules: getRulesPayload(values),
                removeWhenRulesNoLongerMet: !values.canBeEarnedByMembers
                  ? true
                  : !!values.removeWhenRulesNoLongerMet,
                manuallyAssignedSupporterIds:
                  values.giveToSelectedMembers && values.manuallyAssignedSupporters.length
                    ? values.manuallyAssignedSupporters.map((supporter) => supporter.id)
                    : undefined,
                creatorApplicationSettings: values.giveToSelectedTeamStaff
                  ? {
                      type: 'ApplyToSelectedEmails',
                      emails: values.manuallyAssignedTeamStaff,
                    }
                  : {
                      type: 'NoSettings',
                    },
              },
            ],
            {
              onSuccess: () => {
                badgesQuery.refetch()
                toast.notify(
                  'Badge has been updated successfully! It may take up to 2 minutes to reflect changes',
                  { type: 'success' },
                )
              },
            },
          )
        } else if (badge?.type === 'Verified') {
          editVerifiedBadgeMutation.mutate(
            [
              {
                text: values.text,
                backgroundColor: values.backgroundColor,
                textColor: values.textColor,
                usernameColor: values.useCustomUsernameColor ? values.usernameTextColor : undefined,
                creatorApplicationSettings:
                  values.verifiedApplyToType === 'Selected'
                    ? {
                        type: 'ApplyToSelectedEmails',
                        emails: values.manuallyAssignedTeamStaff,
                      }
                    : {
                        type: 'ApplyToAll',
                      },
              },
            ],
            {
              onSuccess: () => {
                badgesQuery.refetch()
                toast.notify(
                  'Badge has been updated successfully! It may take up to 2 minutes to reflect changes',
                  { type: 'success' },
                )
              },
            },
          )
        } else {
          editMemberStreakBadgeMutation.mutate(
            [
              {
                badges: [
                  {
                    id: parseInt(badgeId),
                    icon: getIconPayload(values.emoji),
                    borderColor: values.borderColor,
                    backgroundColor: values.backgroundColor,
                    textColor: values.textColor,
                    minimal: values.badgeType === 'IconOnly',
                  },
                  ...values.memberStreakBadges!.map((memberStreakBadge) => ({
                    id: memberStreakBadge.id,
                    icon: getIconPayload(memberStreakBadge.emoji),
                    minimal: values.badgeType === 'IconOnly',
                    borderColor: memberStreakBadge.borderColor,
                    backgroundColor: memberStreakBadge.backgroundColor,
                    textColor: memberStreakBadge.textColor,
                    monthsFrom: memberStreakBadge.monthsFrom || undefined,
                    monthsTo: memberStreakBadge.monthsTo || undefined,
                  })),
                ],
              },
            ],
            {
              onSuccess: () => {
                badgesQuery.refetch()
                toast.notify(
                  'Badge has been updated successfully! It may take up to 2 minutes to reflect changes',
                  { type: 'success' },
                )
              },
            },
          )
        }
      },
    })

  const value = useMemo(
    () => ({
      badge,
      tiers: tiersQuery.data?.tiers,
      form,
      availableRules,
      hasEmptyRule,
      hasAnyRules,
      hasAvailableRules,
      usersQuery,
      isFetching: !badgesQuery.data?.badges || !tiersQuery.data || !usersQuery.data,
      isLoading:
        editCustomBadgeMutation.isLoading ||
        enableCustomBadgeMutation.isLoading ||
        disableCustomBadgeMutation.isLoading ||
        deleteCustomBadgeMutation.isLoading ||
        editMemberStreakBadgeMutation.isLoading ||
        editVerifiedBadgeMutation.isLoading ||
        enableVerifiedBadgeMutation.isLoading ||
        disableVerifiedBadgeMutation.isLoading,
      enableBadge,
      disableBadge,
      deleteBadge,
      getRuleComponent,
    }),
    [
      badge,
      tiersQuery.data,
      form,
      availableRules,
      hasEmptyRule,
      hasAnyRules,
      hasAvailableRules,
      usersQuery,
      badgesQuery.data?.badges,
      editCustomBadgeMutation.isLoading,
      enableCustomBadgeMutation.isLoading,
      disableCustomBadgeMutation.isLoading,
      deleteCustomBadgeMutation.isLoading,
      enableVerifiedBadgeMutation.isLoading,
      disableVerifiedBadgeMutation.isLoading,
      editMemberStreakBadgeMutation.isLoading,
      editVerifiedBadgeMutation.isLoading,
      enableBadge,
      disableBadge,
      deleteBadge,
      getRuleComponent,
    ],
  )

  return <BadgeContext.Provider value={value}>{children}</BadgeContext.Provider>
}
